import { Engine, Fuel } from '@common/enums';
import { formatString } from '@common/utils';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import { VehicleType } from '~/@store/vehicleTypes';
import { ProjectWeightReceiptInfo } from '~/@user-store/weightReceipts/projectWeightReceipts.store';
import { IDisplayDeliveryLineStatus } from '~/@views/UserView/ProjectPage/common/DeliveryLineEditForm/DeliveryLineEditStatusSelect';
import { IProject } from '~/@views/UserView/ProjectPage/ProjectMassesDetails/types';
import { ProjectQuery_project_dumpLoads } from '~/graphql';
import i18n from '~/i18n';

export type DeliveryLineDumpLoad = Pick<ProjectQuery_project_dumpLoads, 'id' | 'status' | 'serialNumber' | 'inbound'>;

export type IDeliveryLineFormValues = {
    project: IProject;
    deliveryLine?: ProjectWeightReceiptInfo;
    dumpLoad: DeliveryLineDumpLoad | null;
    weight: number | null;
    date: Date | null;
    truckRegistrationNumber: string;
    truckCapacity: number | null;
    receipt: File | null;
    senderComment: string;
    driverComment: string;
    vehicleType: VehicleType | null;
    fuel: Fuel | null;
    engine: Engine | null;
    status: IDisplayDeliveryLineStatus;
    isVerified: boolean;
};

export const getDeliveryLineValidationSchema = (
    canCreateDeliveryLine?: (order: DeliveryLineDumpLoad) => boolean,
    deliveryLine?: { receiptUrl: string | null }
) =>
    Yup.object().shape({
        dumpLoad: Yup.object()
            .nullable()
            .required(i18n.errorCodes.DELIVERY_LINE_ORDER_CANNOT_BE_EMPTY)
            .test('Permission validation', i18n.errorCodes.DELIVERY_LINE_DUMP_LOAD_IS_DONE, function (this, order) {
                if (!order || !canCreateDeliveryLine) return true;

                return canCreateDeliveryLine(order as DeliveryLineDumpLoad);
            }),
        weight: Yup.number().moreThan(0).required(i18n.errorCodes.DELIVERY_LINE_WEIGHT_CANNOT_BE_EMPTY),
        date: Yup.date().nullable().required(i18n.errorCodes.DELIVERY_LINE_DATE_CANNOT_BE_EMPTY),
        truckRegistrationNumber: Yup.string()
            .nullable()
            .trim()
            .required(i18n.errorCodes.DELIVERY_LINE_TRUCK_NUMBER_CANNOT_BE_EMPTY),
        truckCapacity: Yup.number().moreThan(
            0,
            formatString(i18n.mustBeGreaterThanZero, i18n.DeliveryLinesTable.truckCapacity)
        ),
        vehicleType: Yup.object().nullable().required(i18n.errorCodes.DELIVERY_LINE_VEHICLE_TYPE_CANNOT_BE_EMPTY),
        fuel: Yup.string().required(i18n.errorCodes.DELIVERY_LINE_FUEL_CANNOT_BE_EMPTY),
        engine: Yup.string().required(i18n.errorCodes.DELIVERY_LINE_ENGINE_CANNOT_BE_EMPTY),
        receipt: Yup.mixed()
            .nullable()
            .test(
                'Receipt validation',
                i18n.errorCodes.DELIVERY_LINE_RECEIPT_CANNOT_BE_EMPTY,
                function (this, receipt) {
                    return !!receipt || !!deliveryLine?.receiptUrl;
                }
            ),
    });

export const useDeliveryLineFormikContext = () => useFormikContext<IDeliveryLineFormValues>();
